/* eslint-disable @typescript-eslint/no-use-before-define --
   Helpers are grouped by concern and sit below their callers; hoisted
   `function` declarations make that ordering safe at runtime. */
import {
  redirectToGenesysAuth0,
  SilentInteractionRequiredError,
} from "./genesys-oauth";
// Genesys Cloud Web Messaging transport in headless mode. Exposes a
// small transport API (start/send/clear/typing) plus a listener hook
// for inbound events. The V2 `ChatClient` bridge lives in
// `emblem-live-chat-client.ts`.

// Per-env values from `.env.*`. Deployment must have
// `headlessMode.enabled: true` set in Genesys Cloud Admin.
const GENESYS_DEPLOYMENT_ID = import.meta.env.VITE_GENESYS_DEPLOYMENT_ID as
  | string
  | undefined;

const GENESYS_ENVIRONMENT =
  (import.meta.env.VITE_GENESYS_ENVIRONMENT as string | undefined) ??
  "prod-usw2";

const GENESYS_BOOTSTRAP_URL =
  (import.meta.env.VITE_GENESYS_BOOTSTRAP_URL as string | undefined) ??
  "https://apps.usw2.pure.cloud/genesys-bootstrap/genesys.min.js";

const GENESYS_GLOBAL = "Genesys" as const;

// Bootstrap ready timeout — reject `loadGenesysMessenger` and clear
// the memoized promise so the next user action can retry.
const GENESYS_READY_TIMEOUT_MS = 30_000;

// Session-start watchdog escalation ladder. After
// `MessagingService.startConversation` we expect `.started`,
// `.restored`, or `.error` within the primary window. On timeout we
// escalate: (1) `AuthProvider.reauthenticate` as a high-priority
// interrupt (may cut through a wedged LOGGING_OUT state), then
// (2) `Auth.logout` as the hard-fallback that surfaces
// CHAT_UNAVAILABLE via the Auth.loggedOut cascade.
const SESSION_START_WATCHDOG_MS = 10_000;
const REAUTHENTICATE_ESCALATION_MS = 5_000;

type GenesysCommandName =
  | "MessagingService.startConversation"
  | "MessagingService.sendMessage"
  | "MessagingService.clearConversation"
  | "MessagingService.clearSession"
  | "MessagingService.resetConversation"
  | "MessagingService.sendTyping"
  | "MessagingService.clearTypingTimeout"
  | "Database.set"
  | "Auth.logout"
  | "AuthProvider.reAuthenticate";

type GenesysSubscribeEvent =
  | "MessagingService.ready"
  | "MessagingService.started"
  | "MessagingService.restored"
  | "MessagingService.reconnected"
  | "MessagingService.messagesReceived"
  | "MessagingService.typingReceived"
  | "MessagingService.typingTimeout"
  | "MessagingService.conversationDisconnected"
  | "MessagingService.conversationCleared"
  | "MessagingService.sessionWarning"
  | "MessagingService.customAttributesSizeExceeded"
  | "MessagingService.error";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AuthProviderPlugin = any;

type GenesysCommand = (
  action: "command",
  name: GenesysCommandName,
  payload?: Record<string, unknown>,
  onSuccess?: (result?: unknown) => void,
  onError?: (error?: unknown) => void
) => void;

type GenesysSubscribe = (
  action: "subscribe",
  event: GenesysSubscribeEvent,
  handler: (payload?: { data?: unknown }) => void
) => void;

type GenesysRegisterPlugin = (
  action: "registerPlugin",
  name: "AuthProvider",
  factory: (provider: AuthProviderPlugin) => void
) => void;

type GenesysGlobal = GenesysCommand & GenesysSubscribe & GenesysRegisterPlugin;

declare global {
  interface Window {
    Genesys?: GenesysGlobal;
    // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/naming-convention
    _genesysJs?: string;
  }
}

// ---------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------

/**
 * Payload the Genesys `AuthProvider.getAuthCode` command expects back.
 * Produced by `genesys-oauth.ts` via Auth0 PKCE handshake.
 */
export type GenesysAuthCode =
  | {
      authCode: string;
      redirectUri: string;
      codeVerifier: string;
      nonce?: string;
      maxAge?: number;
      iss?: string;
    }
  | {
      idToken: string;
      redirectUri: string;
    };

type GenesysAuthProvider = () => Promise<GenesysAuthCode>;
let authProvider: GenesysAuthProvider | null = null;

/**
 * Registers the auth provider Genesys' SDK calls to authenticate a
 * session. MUST be registered before `startGenesysConversation()`.
 * Last registration wins.
 */
export function registerGenesysAuthProvider(
  provider: GenesysAuthProvider
): void {
  authProvider = provider;
}

// ---------------------------------------------------------------------
// Inbound events → listener
// ---------------------------------------------------------------------

/**
 * Callbacks the bridge implements to translate Genesys events into
 * V2 `InboundEvent`s. The messenger holds a single active listener.
 */
export interface GenesysMessengerListener {
  onReady?(): void;
  onSessionStarted?(payload: {
    newSession: boolean;
    authenticated: boolean;
    readOnly: boolean;
  }): void;
  onSessionEnded?(byAgent: boolean, raw: unknown): void;
  onSessionCleared?(): void;
  onSessionExpiring?(expiresInSeconds: number): void;
  onMessagesReceived?(messages: readonly unknown[]): void;
  onAgentTyping?(durationMs: number): void;
  onAgentTypingTimeout?(): void;
  /**
   * Fires when Genesys' background token refresh returns 401/403.
   * Routed here instead of `onError` so the bridge can trigger a fresh
   * Auth0 handshake without also rendering a raw error banner.
   */
  onSessionFailure?(payload: unknown): void;
  /**
   * Fires when the AuthProvider plugin publishes `Auth.loggedOut`
   * (cross-tab logout, Genesys revoked session, or explicit
   * `AuthProvider.command('Auth.logout')`). Hard session end.
   */
  onAuthLoggedOut?(): void;
  /**
   * Genesys dropped the WHOLE staged custom-attributes map because the
   * total exceeded the deployment's budget. Used as the failure signal
   * for the warm-handoff adapter's proof-of-delivery await.
   */
  onCustomAttributesSizeExceeded?(payload: unknown): void;
  onError?(payload: unknown): void;
}

let listener: GenesysMessengerListener | null = null;

/**
 * Registers the inbound-event listener. Passing `null` clears it.
 * Overwrites any previously registered listener.
 */
export function setGenesysMessengerListener(
  next: GenesysMessengerListener | null
): void {
  listener = next;
}

// ---------------------------------------------------------------------
// Helpers (internal)
// ---------------------------------------------------------------------

function extractRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : undefined;
}

function readBoolean(
  record: Record<string, unknown> | undefined,
  key: string
): boolean | undefined {
  const value = record?.[key];
  return typeof value === "boolean" ? value : undefined;
}

function readNumber(
  record: Record<string, unknown> | undefined,
  key: string
): number | undefined {
  const value = record?.[key];
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

/**
 * Detects "session is dead server-side" (401/403) in a
 * `MessagingService.error` payload. Also checks nested `{errors: [...]}`
 * entries — Genesys wraps some error payloads that way.
 */
function isSessionFailure(data: Record<string, unknown> | undefined): boolean {
  if (!data) return false;
  if (matchesSessionFailure(data)) return true;
  const { errors } = data;
  if (Array.isArray(errors)) {
    return errors.some(
      (entry) =>
        typeof entry === "object" &&
        entry !== null &&
        matchesSessionFailure(entry as Record<string, unknown>)
    );
  }
  return false;
}

function matchesSessionFailure(record: Record<string, unknown>): boolean {
  const { status, code } = record;
  if (typeof status === "number" && (status === 401 || status === 403)) {
    return true;
  }
  // `code` may be numeric (403) or string label ("forbidden").
  if (typeof code === "number" && (code === 401 || code === 403)) {
    return true;
  }
  return code === "forbidden" || code === "unauthorized";
}

// Suppress Genesys' native Messenger UI via CSS. Documented workaround
// gated on the deployment's "User Interface → Off" toggle (Headless SDK
// mode) — currently OFF on our deployment, so Genesys' bootstrap opens
// its own UI panel on top of our `LiveChatContainer` when a conversation
// starts. Selectors cover the `#genesys-mxg-frame` iframe (widget panel)
// and the `[id^="genesys-messenger"]` launcher/DOM the SDK injects.
// Idempotent — subsequent calls no-op via the style-id check.
const GENESYS_WIDGET_STYLE_ID = "emblem-hide-genesys-widget";
function hideGenesysWidgetUntilHeadlessEnabled(): void {
  if (document.getElementById(GENESYS_WIDGET_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = GENESYS_WIDGET_STYLE_ID;
  style.textContent = `
    #genesys-mxg-frame,
    [id^="genesys-messenger"],
    [class*="genesys-messenger"] {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
}

function runCommand(
  name: GenesysCommandName,
  payload: Record<string, unknown>
): Promise<void> {
  return new Promise((resolve, reject) => {
    window.Genesys!(
      "command",
      name,
      payload,
      () => resolve(),
      (err) => reject(err ?? new Error(`Genesys command ${name} rejected`))
    );
  });
}

// ---------------------------------------------------------------------
// DEV diagnostic: capture 4xx / error frames from any `*.pure.cloud/*`
// endpoint (fetch, XHR, WS). Genesys' SDK swallows these internally, so
// the only way to grab a `contextId` for a Genesys support ticket
// without this is via DevTools. Idempotent, DEV-only.
// ---------------------------------------------------------------------
const GENESYS_HOST_MATCHER = /\.pure\.cloud\//;

function logGenesys4xx(
  status: number,
  url: string,
  bodyText: string,
  transport: "fetch" | "xhr" | "ws"
): void {
  let contextId: string | undefined;
  let code: string | undefined;
  let message: string | undefined;
  try {
    const json = JSON.parse(bodyText) as {
      contextId?: unknown;
      code?: unknown;
      message?: unknown;
    };
    contextId = typeof json.contextId === "string" ? json.contextId : undefined;
    code = typeof json.code === "string" ? json.code : undefined;
    message = typeof json.message === "string" ? json.message : undefined;
  } catch {
    // Non-JSON body — raw text still logged below.
  }
  // Pass fields as separate `%s` args (not interpolated) — CodeQL
  // flags interpolation as a format-string sink because the values
  // come from a 4xx body and could carry unescaped format tokens.
  // eslint-disable-next-line no-console
  console.error(
    "[genesys %s][%s] contextId: %s | code: %s | message: %s",
    status,
    transport,
    contextId ?? "<none>",
    code ?? "<none>",
    message ?? "<none>",
    { url, bodyText }
  );
}

let genesysFetchDebugInstalled = false;
function installGenesysFetchDebug(): void {
  if (genesysFetchDebugInstalled) return;
  if (!import.meta.env.DEV) return;
  if (typeof window === "undefined") return;
  genesysFetchDebugInstalled = true;

  // --- fetch proxy ---
  if (typeof window.fetch === "function") {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      const response = await originalFetch(input, init);
      let url: string;
      if (typeof input === "string") {
        url = input;
      } else if (input instanceof URL) {
        url = input.toString();
      } else {
        url = input.url;
      }
      if (
        response.status >= 400 &&
        response.status < 500 &&
        GENESYS_HOST_MATCHER.test(url)
      ) {
        // Clone before reading — the SDK still needs to consume the body.
        const bodyText = await response
          .clone()
          .text()
          .catch(() => "");
        logGenesys4xx(response.status, url, bodyText, "fetch");
      }
      return response;
    };
  }

  // --- XMLHttpRequest proxy ---
  // Genesys' cxbus.min.js uses XHR on some SDK versions.
  if (typeof window.XMLHttpRequest === "function") {
    const OriginalXHR = window.XMLHttpRequest;
    const originalOpen = OriginalXHR.prototype.open;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (OriginalXHR.prototype as any).open = function patchedOpen(
      this: XMLHttpRequest,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...args: any[]
    ) {
      const url = typeof args[1] === "string" ? args[1] : String(args[1]);
      if (GENESYS_HOST_MATCHER.test(url)) {
        this.addEventListener("loadend", () => {
          if (this.status >= 400 && this.status < 500) {
            const bodyText =
              typeof this.responseText === "string" ? this.responseText : "";
            logGenesys4xx(this.status, url, bodyText, "xhr");
          }
        });
      }
      // eslint-disable-next-line prefer-spread, @typescript-eslint/no-explicit-any
      return originalOpen.apply(this, args as any);
    };
  }

  // --- WebSocket proxy ---
  // Genesys' `wss://webmessaging.*.pure.cloud/` carries auth challenges
  // inside frame payloads that HTTP proxies won't see. Non-Genesys
  // sockets pass through untouched.
  if (typeof window.WebSocket === "function") {
    const OriginalWebSocket = window.WebSocket;
    const PatchedWebSocket = function PatchedWebSocket(
      this: WebSocket,
      url: string | URL,
      protocols?: string | string[]
    ) {
      const ws = new OriginalWebSocket(url, protocols);
      const urlStr = typeof url === "string" ? url : url.toString();
      if (GENESYS_HOST_MATCHER.test(urlStr) || /webmessaging/.test(urlStr)) {
        ws.addEventListener("message", (evt) => {
          if (typeof evt.data !== "string") return;
          const looksLikeError =
            /"status"\s*:\s*4\d\d/.test(evt.data) ||
            /"code"\s*:\s*"(?:forbidden|unauthorized)"/i.test(evt.data) ||
            /"code"\s*:\s*4\d\d/.test(evt.data);
          if (looksLikeError) {
            logGenesys4xx(0, urlStr, evt.data, "ws");
          }
        });
        ws.addEventListener("close", (evt) => {
          // 1000/1001 = normal. Anything else on a Genesys socket is
          // worth surfacing.
          if (evt.code !== 1000 && evt.code !== 1001) {
            // Pass fields as separate `%s` args — CodeQL flags
            // interpolation as a format-string sink because `evt.reason`
            // is server-controlled.
            // eslint-disable-next-line no-console
            console.error(
              "[genesys ws-close][code:%s] reason: %s",
              evt.code,
              evt.reason || "<none>",
              { url: urlStr }
            );
          }
        });
      }
      return ws;
    } as unknown as typeof WebSocket;
    PatchedWebSocket.prototype = OriginalWebSocket.prototype;
    // Preserve static constants so consumers reading `WebSocket.OPEN`
    // etc. still get the same values.
    (PatchedWebSocket as unknown as { CONNECTING: number }).CONNECTING =
      OriginalWebSocket.CONNECTING;
    (PatchedWebSocket as unknown as { OPEN: number }).OPEN =
      OriginalWebSocket.OPEN;
    (PatchedWebSocket as unknown as { CLOSING: number }).CLOSING =
      OriginalWebSocket.CLOSING;
    (PatchedWebSocket as unknown as { CLOSED: number }).CLOSED =
      OriginalWebSocket.CLOSED;
    window.WebSocket = PatchedWebSocket;
  }

  // eslint-disable-next-line no-console
  console.info(
    "[genesys-debug] fetch/XHR/WS 4xx proxy installed for *.pure.cloud/*"
  );
}

// ---------------------------------------------------------------------
// Session-start watchdog — see escalation ladder above.
// ---------------------------------------------------------------------

let sessionStartWatchdogTimer: ReturnType<typeof setTimeout> | null = null;

function armSessionStartWatchdog(): void {
  clearSessionStartWatchdog();
  sessionStartWatchdogTimer = setTimeout(() => {
    sessionStartWatchdogTimer = null;
    // Stage 1: reauthenticate (lighter touch; doesn't tear down auth
    // session, so it doesn't deepen a LOGGING_OUT wedge).
    // eslint-disable-next-line no-console
    console.error(
      "[genesys] watchdog: no session-live signal — firing AuthProvider.reauthenticate"
    );
    window.Genesys?.("command", "AuthProvider.reAuthenticate");
    // Stage 2: Auth.logout as hard fallback → Auth.loggedOut cascade →
    // CHAT_UNAVAILABLE.
    sessionStartWatchdogTimer = setTimeout(() => {
      sessionStartWatchdogTimer = null;
      // eslint-disable-next-line no-console
      console.error(
        "[genesys] watchdog: reauthenticate didn't recover — falling back to Auth.logout"
      );
      window.Genesys?.("command", "Auth.logout");
    }, REAUTHENTICATE_ESCALATION_MS);
  }, SESSION_START_WATCHDOG_MS);
}

export function clearSessionStartWatchdog(): void {
  if (sessionStartWatchdogTimer !== null) {
    clearTimeout(sessionStartWatchdogTimer);
    sessionStartWatchdogTimer = null;
  }
}

// ---------------------------------------------------------------------
// `conversationCleared` waiter. `runCommand` settles when Genesys' bus
// ACCEPTS the command, not when the server-side clear completes. When
// the `.conversationCleared` event lands between a subsequent
// startConversation dispatch and its fulfillment, Genesys' state
// machine silently drops the start. Arm this waiter BEFORE dispatching
// clearConversation so the server-side confirmation is observed first.
// ---------------------------------------------------------------------

type ClearedResolver = (cleared: boolean) => void;
const pendingClearedResolvers = new Set<ClearedResolver>();

function fireConversationClearedResolvers(): void {
  if (pendingClearedResolvers.size === 0) return;
  const snapshot = Array.from(pendingClearedResolvers);
  pendingClearedResolvers.clear();
  snapshot.forEach((resolve) => resolve(true));
}

/**
 * Resolves `true` on the next `MessagingService.conversationCleared`
 * event, or `false` on timeout. Call BEFORE dispatching a
 * `clearConversation` command that might fire it. Self-cleaning.
 */
export function waitForNextConversationCleared(
  timeoutMs: number
): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const settle = (cleared: boolean) => {
      if (timer !== undefined) clearTimeout(timer);
      pendingClearedResolvers.delete(resolver);
      resolve(cleared);
    };
    const resolver: ClearedResolver = () => settle(true);
    timer = setTimeout(() => settle(false), timeoutMs);
    pendingClearedResolvers.add(resolver);
  });
}

// ---------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------

let bootstrapPromise: Promise<void> | null = null;

/**
 * Injects Genesys' bootstrap snippet once per page load. Resolves once
 * `MessagingService.ready` fires. On rejection the memoized promise is
 * cleared so a subsequent call can retry.
 */
function loadGenesysMessenger(): Promise<void> {
  installGenesysFetchDebug();

  if (bootstrapPromise) return bootstrapPromise;

  if (!GENESYS_DEPLOYMENT_ID || !GENESYS_ENVIRONMENT) {
    // Config-missing is permanent within the session — no retry point.
    bootstrapPromise = Promise.reject(
      new Error(
        "Genesys Messenger not configured — set VITE_GENESYS_DEPLOYMENT_ID and VITE_GENESYS_ENVIRONMENT."
      )
    );
    return bootstrapPromise;
  }

  const boot = new Promise<void>((resolveRaw, rejectRaw) => {
    // Wrap resolve/reject so exactly one settles the promise and the
    // ready-timeout is cleared. Prevents a late `.ready` from silently
    // overwriting an earlier timeout rejection.
    let settled = false;
    let readyTimeout: ReturnType<typeof setTimeout> | null = null;
    const clearReadyTimeout = () => {
      if (readyTimeout !== null) {
        clearTimeout(readyTimeout);
        readyTimeout = null;
      }
    };
    const resolve = () => {
      if (settled) return;
      settled = true;
      clearReadyTimeout();
      resolveRaw();
    };
    const reject = (err: Error) => {
      if (settled) return;
      settled = true;
      clearReadyTimeout();
      rejectRaw(err);
    };

    // Bootstrap-ready timeout — guards against network wedge, CDN
    // outage, CSP block. Clears `bootstrapPromise` so the next user
    // action can retry.
    readyTimeout = setTimeout(() => {
      bootstrapPromise = null;
      reject(
        new Error(
          "Genesys Messenger bootstrap timed out — MessagingService.ready did not fire within 30s."
        )
      );
    }, GENESYS_READY_TIMEOUT_MS);

    // Suppress the native Messenger UI (see `hideGenesysWidgetUntilHeadlessEnabled`).
    hideGenesysWidgetUntilHeadlessEnabled();

    // Genesys' documented bootstrap snippet — do not reformat, do not
    // inline. Sets up the command queue on `window.Genesys` so calls
    // issued before the async script loads are replayed once it does.
    /* eslint-disable
       no-param-reassign,
       no-underscore-dangle,
       func-names,
       @typescript-eslint/no-explicit-any,
       @typescript-eslint/naming-convention */
    (function (g: any, e: string, n: string, es: Record<string, string>) {
      g._genesysJs = e;
      g[e] =
        g[e] ||
        function () {
          // eslint-disable-next-line prefer-rest-params
          (g[e].q = g[e].q || []).push(arguments);
        };
      g[e].t = Date.now();
      g[e].c = es;
      const ys = document.createElement("script");
      ys.async = true;
      ys.src = n;
      ys.charset = "utf-8";
      ys.onerror = () => reject(new Error("Failed to load genesys.min.js"));
      document.head.appendChild(ys);
    })(window, GENESYS_GLOBAL, GENESYS_BOOTSTRAP_URL, {
      environment: GENESYS_ENVIRONMENT!,
      deploymentId: GENESYS_DEPLOYMENT_ID!,
    });
    /* eslint-enable
       no-param-reassign,
       no-underscore-dangle,
       func-names,
       @typescript-eslint/no-explicit-any,
       @typescript-eslint/naming-convention */

    // AuthProvider plugin. Genesys calls `getAuthCode` at handshake
    // time and `reAuthenticate` when the cached token has expired.
    window.Genesys!("registerPlugin", "AuthProvider", (AuthProvider) => {
      AuthProvider.registerCommand(
        "getAuthCode",
        async (e: {
          resolve: (v: GenesysAuthCode) => void;
          reject: (err?: unknown) => void;
        }) => {
          if (!authProvider) {
            e.reject(
              new Error(
                "Genesys AuthProvider not registered — call registerGenesysAuthProvider() before starting a conversation."
              )
            );
            return;
          }
          try {
            e.resolve(await authProvider());
          } catch (err) {
            e.reject(err);
          }
        }
      );

      // `reAuthenticate` fires on 403 from background token refresh, or
      // when the session-start watchdog invokes it. On
      // `SilentInteractionRequiredError` we escalate to a full-page
      // Auth0 redirect (silent iframe can't recover). On other errors
      // we clear Genesys' session so it stops looping on dead creds.
      AuthProvider.registerCommand(
        "reAuthenticate",
        async (e: {
          resolve: (v: GenesysAuthCode) => void;
          reject: (err?: unknown) => void;
        }) => {
          if (!authProvider) {
            e.reject(
              new Error(
                "Genesys AuthProvider not registered — call registerGenesysAuthProvider() before starting a conversation."
              )
            );
            return;
          }
          try {
            e.resolve(await authProvider());
          } catch (err) {
            if (err instanceof SilentInteractionRequiredError) {
              e.reject(err);
              redirectToGenesysAuth0().catch((redirectErr: unknown) => {
                const redirectMsg =
                  redirectErr instanceof Error
                    ? `${redirectErr.name}: ${redirectErr.message}`
                    : String(redirectErr);
                // eslint-disable-next-line no-console
                console.error(
                  "[genesys] auth: redirectToGenesysAuth0 failed after SilentInteractionRequiredError:",
                  redirectMsg
                );
              });
              return;
            }
            window.Genesys?.("command", "MessagingService.clearSession", {});
            e.reject(err);
          }
        }
      );

      // `Auth.authError` / `Auth.loggedOut` are NOT routed through
      // `MessagingService.error` — subscribe on the AuthProvider plugin
      // directly.
      AuthProvider.subscribe("Auth.authError", (payload: unknown) => {
        // eslint-disable-next-line no-console
        console.error(
          "[genesys] Auth.authError — routing to onSessionFailure",
          payload
        );
        listener?.onSessionFailure?.(payload);
      });

      AuthProvider.subscribe("Auth.loggedOut", () => {
        listener?.onAuthLoggedOut?.();
      });

      AuthProvider.ready();
    });

    window.Genesys!("subscribe", "MessagingService.ready", () => {
      listener?.onReady?.();
      resolve();
    });

    window.Genesys!("subscribe", "MessagingService.started", (evt) => {
      clearSessionStartWatchdog();
      const data = extractRecord(evt?.data);
      listener?.onSessionStarted?.({
        newSession: readBoolean(data, "newSession") ?? true,
        authenticated: readBoolean(data, "authenticated") ?? false,
        readOnly: readBoolean(data, "readOnly") ?? false,
      });
    });

    // `.restored` = Genesys resumed a session (page reload, mid-session
    // reauth, WS drop+retry). Translated to `onSessionStarted` with
    // `newSession:false`. Payload carries a limited recent transcript
    // in `messages`; forward so the resumed conversation renders.
    window.Genesys!("subscribe", "MessagingService.restored", (evt) => {
      clearSessionStartWatchdog();
      const data = extractRecord(evt?.data);
      listener?.onSessionStarted?.({
        newSession: false,
        authenticated: true,
        readOnly: readBoolean(data, "readOnly") ?? false,
      });
      const messages = Array.isArray(data?.messages) ? data!.messages : [];
      if (messages.length > 0) listener?.onMessagesReceived?.(messages);
    });

    // `.reconnected` = WS came back after a transient disconnect. No
    // payload; re-emit `onSessionStarted` so the SDK state machine
    // returns to `Started`.
    window.Genesys!("subscribe", "MessagingService.reconnected", () => {
      clearSessionStartWatchdog();
      listener?.onSessionStarted?.({
        newSession: false,
        authenticated: true,
        readOnly: false,
      });
    });

    window.Genesys!("subscribe", "MessagingService.messagesReceived", (evt) => {
      const data = extractRecord(evt?.data);
      const messages = Array.isArray(data?.messages) ? data!.messages : [];
      listener?.onMessagesReceived?.(messages);
    });

    window.Genesys!("subscribe", "MessagingService.typingReceived", (evt) => {
      const data = extractRecord(evt?.data);
      const typing = extractRecord(data?.typing);
      const durationMs = readNumber(typing, "durationMs") ?? 0;
      listener?.onAgentTyping?.(durationMs);
    });

    window.Genesys!("subscribe", "MessagingService.typingTimeout", () => {
      listener?.onAgentTypingTimeout?.();
    });

    window.Genesys!(
      "subscribe",
      "MessagingService.conversationDisconnected",
      (evt) => {
        listener?.onSessionEnded?.(true, evt?.data);
      }
    );

    window.Genesys!("subscribe", "MessagingService.conversationCleared", () => {
      fireConversationClearedResolvers();
      listener?.onSessionCleared?.();
    });

    window.Genesys!("subscribe", "MessagingService.sessionWarning", (evt) => {
      const data = extractRecord(evt?.data);
      // Per docs the payload uses `expirationTime`; `expirationTimestamp`
      // is a cheap fallback against payload-shape drift.
      const expirationMs =
        readNumber(data, "expirationTime") ??
        readNumber(data, "expirationTimestamp");
      const expiresInSeconds =
        expirationMs !== undefined
          ? Math.max(0, Math.round((expirationMs - Date.now()) / 1000))
          : 0;
      listener?.onSessionExpiring?.(expiresInSeconds);
    });

    window.Genesys!(
      "subscribe",
      "MessagingService.customAttributesSizeExceeded",
      (evt) => {
        // eslint-disable-next-line no-console
        console.error(
          "[genesys] customAttributesSizeExceeded — Genesys dropped the whole custom-attributes map",
          evt?.data
        );
        listener?.onCustomAttributesSizeExceeded?.(evt?.data);
      }
    );

    window.Genesys!("subscribe", "MessagingService.error", (evt) => {
      const data = extractRecord(evt?.data);
      // Route 401/403 to `onSessionFailure` so the bridge can react
      // (wipe + redirect to Auth0) without ALSO rendering a raw error
      // banner. Non-session errors (429, 5xx, etc.) forward to `onError`.
      if (isSessionFailure(data)) {
        // eslint-disable-next-line no-console
        console.error(
          "[genesys] session-failure error from MessagingService — routing to onSessionFailure",
          evt?.data
        );
        listener?.onSessionFailure?.(evt?.data);
        return;
      }
      listener?.onError?.(evt?.data);
    });
  });

  // Clear the memoized promise on rejection so a subsequent call can
  // retry after transient failures (network blip, CDN 5xx).
  bootstrapPromise = boot.catch((err) => {
    bootstrapPromise = null;
    throw err;
  });

  return bootstrapPromise;
}

// ---------------------------------------------------------------------
// Outbound commands
// ---------------------------------------------------------------------

/**
 * Starts a Genesys conversation. `customAttributes`, when provided,
 * are written to Genesys' database before the JOIN so agents see them
 * (the warm-transfer channel).
 */
export async function startGenesysConversation(
  customAttributes?: Record<string, unknown>
): Promise<void> {
  await loadGenesysMessenger();

  // Watchdog cleared on `.started`/`.restored`/`.reconnected`. On
  // timeout the escalation ladder ultimately fires `Auth.logout` →
  // `Auth.loggedOut` → bridge emits `CHAT_UNAVAILABLE`.
  armSessionStartWatchdog();

  if (customAttributes && Object.keys(customAttributes).length > 0) {
    // `Database.set` before start so attributes ride the JOIN event.
    // The nested `messaging.customAttributes` shape is required —
    // a flat `messagingCustomAttributes` key is silently ignored.
    // Non-fatal: size overruns surface via
    // `customAttributesSizeExceeded`, other failures via `.error`.
    await runCommand("Database.set", {
      messaging: { customAttributes },
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error(
        "[genesys] Database.set (warm-transfer attributes) rejected; conversation will still start",
        err
      );
    });
  }

  await runCommand("MessagingService.startConversation", {}).catch((err) => {
    // Command rejected before any session-lifecycle event — disarm the
    // watchdog so it doesn't escalate against a call already known to
    // have failed. Bridge surfaces the rejection via
    // `handleUserStartChat`'s startPromise.catch.
    clearSessionStartWatchdog();
    throw err;
  });
}

export async function sendGenesysMessage(text: string): Promise<void> {
  await loadGenesysMessenger();
  await runCommand("MessagingService.sendMessage", { message: text });
}

export async function sendGenesysQuickReply(reply: {
  text: string;
  payload: string;
  action?: string;
}): Promise<void> {
  await loadGenesysMessenger();
  await runCommand("MessagingService.sendMessage", {
    type: "quickReply",
    postback: {
      action: reply.action ?? "Message",
      text: reply.text,
      payload: reply.payload,
    },
  });
}

export async function clearGenesysConversation(): Promise<void> {
  // Intentionally does NOT clear the session-start watchdog. The warm-
  // handoff adapter's timeout path calls this ~500ms before the
  // watchdog is due to escalate; clearing here would cancel the
  // reauthenticate/Auth.logout ladder that exists to repair the WS-403
  // that caused the timeout. Watchdog self-disarms on happy-path
  // events and is superseded by the next `startGenesysConversation`.
  await loadGenesysMessenger();
  await runCommand("MessagingService.clearConversation", {});
}

/**
 * Ends the member's Genesys session, not just the conversation, so the
 * next `startConversation` opens a new session (`newSession: true`) and
 * the deployment's flow runs with the staged attributes. Web's stand-in
 * for Android's `startSeparateSession` (drops the vault's session
 * token), which our JS can't do — the token lives in Genesys' iframe.
 * After `clearConversation` alone, the restart reconnects to the same
 * session (`.restored` with an empty transcript) and nothing routes.
 * On UAT the command takes effect but never settles — callers must not
 * await it unbounded.
 */
export async function clearGenesysSession(): Promise<void> {
  await loadGenesysMessenger();
  await runCommand("MessagingService.clearSession", {});
}

/**
 * Ends the disconnected conversation Genesys resumed onto and starts
 * fresh. Only works when the deployment's "Read-only conversation
 * disconnect" flag is enabled. Bridge calls this on `.started` with
 * `readOnly: true` to force a writable session before CHAT_STARTED.
 */
export async function resetGenesysConversation(): Promise<void> {
  await loadGenesysMessenger();
  await runCommand("MessagingService.resetConversation", {});
}

/**
 * Signals user typing to Genesys. Fire-and-forget: typing signals are
 * lossy by design; queued if the SDK isn't ready yet.
 */
export function setGenesysTyping(isTyping: boolean): void {
  if (!bootstrapPromise) return;
  const name = isTyping
    ? "MessagingService.sendTyping"
    : "MessagingService.clearTypingTimeout";
  window.Genesys?.("command", name, {});
}

// ---------------------------------------------------------------------
// Logout cleanup
// ---------------------------------------------------------------------

function clearGenesysLocalStorage(): void {
  if (!GENESYS_DEPLOYMENT_ID) return;
  try {
    const prefix = `_${GENESYS_DEPLOYMENT_ID}:`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(prefix)) keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // localStorage inaccessible — don't block logout.
  }
}

// Conversations plugin key that resumes a disconnected conversation on
// next `MessagingService.start`. Wiping it forces a fresh conversation
// and prevents the `readOnly: true` resume path.
const GENESYS_CONVERSATION_ACTIVE_KEY_SUFFIX = "gcmcsessionActive";

/**
 * Wipes Genesys' conversation-continuation key so the SDK cannot
 * resume a closed conversation on next start (which would manifest as
 * `readOnly: true` + every send returning 409). Idempotent.
 */
export function wipeGenesysSessionState(): void {
  if (!GENESYS_DEPLOYMENT_ID) return;
  try {
    const key = `_${GENESYS_DEPLOYMENT_ID}:${GENESYS_CONVERSATION_ACTIVE_KEY_SUFFIX}`;
    if (window.localStorage.getItem(key) === null) return;
    window.localStorage.removeItem(key);
  } catch {
    // localStorage inaccessible — nothing to do.
  }
}

/**
 * Terminates the Genesys session and wipes client-side state. Call
 * before app logout to prevent cross-user leaks and zombie sessions.
 *
 * Returns a promise that resolves once `clearConversation` settles so
 * callers can chain (e.g. readOnly-recovery restart). Client-side
 * wipes and the watchdog cancel run synchronously — those apply even
 * if the caller ignores the promise. Module-scope state
 * (`bootstrapPromise`, `listener`, `authProvider`) is intentionally
 * kept: bootstrap is a page-lifetime singleton and duplicate event
 * subscriptions would leak; Auth0 logout normally reloads the page.
 */
export function terminateGenesysSession(): Promise<void> {
  // Cancel watchdog — logout supersedes any in-flight escalation.
  clearSessionStartWatchdog();
  const clearPromise = window.Genesys
    ? runCommand("MessagingService.clearConversation", {}).catch(() => {
        // Benign — server may already be clear.
      })
    : Promise.resolve();
  clearGenesysLocalStorage();
  return clearPromise;
}
